import React, { useState } from "react";
import axios from "../../api/axiosInstance";
import toast, { Toaster } from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { GiLotus } from "react-icons/gi";
import {
  FaEnvelope,
  FaKey,
  FaLock,
  FaEye,
  FaEyeSlash,
  FaCheck,
} from "react-icons/fa";

const ForgotPassword = () => {
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const navigate = useNavigate();

  const handleSendOTP = async () => {
    if (!email) {
      toast.error("Please enter your email.");
      return;
    }
    setLoading(true);
    try {
      await axios.post("/api/accounts/forgot-password/send-otp/", { email });
      toast.success("OTP sent to your email.");
      setStep(2);
    } catch (error) {
      toast.error(
        error.response?.data?.error || "Failed to send OTP. Please try again.",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async () => {
    if (!otp) {
      toast.error("Please enter the OTP.");
      return;
    }
    setLoading(true);
    try {
      await axios.post("/api/accounts/forgot-password/verify-otp/", {
        email,
        otp,
      });
      toast.success("OTP verified. You can now reset your password.");
      setStep(3);
    } catch (error) {
      toast.error(
        error.response?.data?.error ||
          "Failed to verify OTP. Please try again.",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async () => {
    if (!newPassword || !confirmPassword) {
      toast.error("Please fill in all password fields.");
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error("Passwords do not match.");
      return;
    }
    setLoading(true);
    try {
      await axios.post("/api/accounts/forgot-password/reset/", {
        email,
        new_password: newPassword,
        confirm_password: confirmPassword,
      });
      toast.success("Password reset successfully. Please login.");
      navigate("/login");
    } catch (error) {
      toast.error(
        error.response?.data?.error ||
          "Failed to reset password. Please try again.",
      );
    } finally {
      setLoading(false);
    }
  };

  const steps = [
    { number: 1, label: "Email", icon: FaEnvelope },
    { number: 2, label: "OTP", icon: FaKey },
    { number: 3, label: "Reset", icon: FaLock },
  ];

  return (
    <div className="flex items-center justify-center bg-gradient-to-br from-amber-50 via-orange-50 to-rose-50 px-4 py-8 sm:py-10 relative overflow-hidden">
      <Toaster position="top-center" />

      {/* Decorative lotus watermarks, hidden on very small screens */}
      <GiLotus className="hidden sm:block absolute -top-8 -left-8 text-amber-200/40 text-[140px] sm:text-[180px] rotate-12 pointer-events-none" />
      <GiLotus className="hidden sm:block absolute -bottom-10 -right-6 text-orange-200/40 text-[140px] sm:text-[180px] -rotate-12 pointer-events-none" />

      <div className="relative z-10 bg-white/95 backdrop-blur-sm shadow-xl ring-1 ring-amber-100 rounded-2xl w-full max-w-sm sm:max-w-md p-6 sm:p-8">
        {/* Brand mark */}
        <div className="flex flex-col items-center mb-4">
          <div className="bg-gradient-to-br from-amber-400 to-orange-600 text-white rounded-full p-3 mb-3 shadow-md">
            <FaLock className="text-xl sm:text-2xl" />
          </div>
          <span className="uppercase tracking-wide text-xs font-medium text-amber-700">
            Jain Var Vadhu
          </span>
        </div>

        <h2 className="text-xl sm:text-2xl font-bold text-center text-orange-700 mb-5">
          Forgot Password
        </h2>

        {/* Step progress indicator */}
        <div className="flex items-center justify-center mb-7">
          {steps.map((s, idx) => {
            const Icon = s.icon;
            const isComplete = step > s.number;
            const isActive = step === s.number;
            return (
              <React.Fragment key={s.number}>
                <div className="flex flex-col items-center">
                  <div
                    className={`w-8 h-8 sm:w-9 sm:h-9 rounded-full flex items-center justify-center text-xs sm:text-sm transition-colors ${
                      isComplete
                        ? "bg-orange-600 text-white"
                        : isActive
                          ? "bg-gradient-to-br from-amber-400 to-orange-600 text-white shadow-md"
                          : "bg-amber-100 text-amber-400"
                    }`}
                  >
                    {isComplete ? (
                      <FaCheck className="text-xs" />
                    ) : (
                      <Icon className="text-xs sm:text-sm" />
                    )}
                  </div>
                  <span
                    className={`mt-1.5 text-[10px] sm:text-xs font-medium ${
                      isActive || isComplete
                        ? "text-orange-700"
                        : "text-amber-400"
                    }`}
                  >
                    {s.label}
                  </span>
                </div>
                {idx < steps.length - 1 && (
                  <div
                    className={`h-0.5 w-8 sm:w-14 mx-1 sm:mx-2 -mt-4 transition-colors ${
                      step > s.number ? "bg-orange-500" : "bg-amber-100"
                    }`}
                  />
                )}
              </React.Fragment>
            );
          })}
        </div>

        {step === 1 && (
          <>
            <p className="text-sm text-gray-500 mb-4 text-center">
              Enter your registered email to receive an OTP.
            </p>
            <div className="relative mb-4">
              <FaEnvelope className="absolute left-3 top-1/2 -translate-y-1/2 text-amber-400 text-sm" />
              <input
                type="email"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full border border-amber-200 rounded-lg pl-9 pr-3 py-2.5 text-sm sm:text-base focus:outline-none focus:ring-2 focus:ring-orange-300 focus:border-orange-400 transition-colors"
              />
            </div>
            <button
              onClick={handleSendOTP}
              disabled={loading}
              className={`w-full bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white font-semibold py-2.5 sm:py-3 rounded-full text-sm sm:text-base transition-colors ${
                loading ? "opacity-50 cursor-not-allowed" : ""
              }`}
            >
              {loading ? "Sending OTP..." : "Send OTP"}
            </button>
          </>
        )}

        {step === 2 && (
          <>
            <p className="text-sm text-gray-500 mb-4 text-center">
              Enter the OTP sent to{" "}
              <span className="font-medium text-gray-700 break-all">
                {email}
              </span>
            </p>
            <div className="relative mb-4">
              <FaKey className="absolute left-3 top-1/2 -translate-y-1/2 text-amber-400 text-sm" />
              <input
                type="text"
                placeholder="Enter OTP"
                value={otp}
                onChange={(e) => setOtp(e.target.value)}
                className="w-full border border-amber-200 rounded-lg pl-9 pr-3 py-2.5 text-sm sm:text-base text-center tracking-widest focus:outline-none focus:ring-2 focus:ring-orange-300 focus:border-orange-400 transition-colors"
              />
            </div>
            <button
              onClick={handleVerifyOTP}
              disabled={loading}
              className={`w-full bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white font-semibold py-2.5 sm:py-3 rounded-full text-sm sm:text-base transition-colors ${
                loading ? "opacity-50 cursor-not-allowed" : ""
              }`}
            >
              {loading ? "Verifying..." : "Verify OTP"}
            </button>
          </>
        )}

        {step === 3 && (
          <>
            <p className="text-sm text-gray-500 mb-4 text-center">
              Enter your new password.
            </p>
            <div className="relative mb-3">
              <FaLock className="absolute left-3 top-1/2 -translate-y-1/2 text-amber-400 text-sm" />
              <input
                type={showNewPassword ? "text" : "password"}
                placeholder="New Password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="w-full border border-amber-200 rounded-lg pl-9 pr-10 py-2.5 text-sm sm:text-base focus:outline-none focus:ring-2 focus:ring-orange-300 focus:border-orange-400 transition-colors"
              />
              <button
                type="button"
                onClick={() => setShowNewPassword(!showNewPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-amber-400 hover:text-orange-500 text-sm"
                tabIndex={-1}
              >
                {showNewPassword ? <FaEyeSlash /> : <FaEye />}
              </button>
            </div>
            <div className="relative mb-4">
              <FaLock className="absolute left-3 top-1/2 -translate-y-1/2 text-amber-400 text-sm" />
              <input
                type={showConfirmPassword ? "text" : "password"}
                placeholder="Confirm New Password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="w-full border border-amber-200 rounded-lg pl-9 pr-10 py-2.5 text-sm sm:text-base focus:outline-none focus:ring-2 focus:ring-orange-300 focus:border-orange-400 transition-colors"
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-amber-400 hover:text-orange-500 text-sm"
                tabIndex={-1}
              >
                {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
              </button>
            </div>
            <button
              onClick={handleResetPassword}
              disabled={loading}
              className={`w-full bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white font-semibold py-2.5 sm:py-3 rounded-full text-sm sm:text-base transition-colors ${
                loading ? "opacity-50 cursor-not-allowed" : ""
              }`}
            >
              {loading ? "Resetting..." : "Reset Password"}
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default ForgotPassword;
